import React, {ReactNode, createContext} from 'react';
import {useTranslation} from 'react-i18next';

import {
    AnchorMenuI18n,
    AppHeaderI18N,
    CoachmarkI18n,
    PaginationI18n,
    SearchI18n,
    SelectI18n,
    SideBarI18n,
    SideMenuI18n,
    StepperI18n,
    TabListI18n,
    TableI18n,
    ToastI18n,
    DatePickerI18n as VeneerDatePickerI18n,
} from '@veneer/core';
import {PasswordI18n} from '@veneer/core/dist/scripts/password/types';
import {NotificationI18n} from '@veneer/core/dist/scripts/shared/notification';

interface DatePickerI18n extends VeneerDatePickerI18n {
    clear: string;
}

interface VeneerTranslations {
    anchorMenu: AnchorMenuI18n;
    appHeader: AppHeaderI18N;
    coachmark: CoachmarkI18n;
    datePicker: DatePickerI18n;
    inlineNotification: NotificationI18n;
    notification: NotificationI18n;
    pagination: PaginationI18n;
    password: PasswordI18n;
    search: SearchI18n;
    select: SelectI18n;
    sideBar: SideBarI18n;
    sideMenu: SideMenuI18n;
    stepper: StepperI18n;
    table: TableI18n;
    tabs: TabListI18n;
    toastNotification: ToastI18n;
}

interface GeneralTranslations {
    logout: string;
    manageAccount: string;
    none: string;
    yes: string;
    no: string;
    yesNo: (val: undefined | boolean | null) => string | undefined;
}

interface GlobalTranslationsContextType {
    general: GeneralTranslations;
    veneer: VeneerTranslations;
}

export const GlobalTranslationsContext = createContext<GlobalTranslationsContextType>(
    {} as GlobalTranslationsContextType
);

export const GlobalTranslationsProvider = ({children}: {children: ReactNode}): JSX.Element => {
    const {t} = useTranslation(['veneer']);
    const generalTranslations: GeneralTranslations = {
        logout: t('veneer:signOut', 'Sign Out'),
        manageAccount: t('veneer:manageAccount', 'Manage Account'),

        // States
        none: t('veneer:none', '(None)'),
        yes: t('veneer:yes', 'Yes'),
        no: t('veneer:no', 'No'),
        yesNo(val: undefined | boolean | null) {
            return val === null || val === undefined
                ? undefined
                : val
                  ? generalTranslations.yes
                  : generalTranslations.no;
        },
    };
    const veneerTranslations: VeneerTranslations = {
        anchorMenu: {
            currentItem: t('veneer:currentItem', 'Current anchor'),
            jumpTo: t('veneer:jumpTo', 'Jump to %{label}'),
        },
        appHeader: {
            toggle: t('veneer:toggle', 'Toggle Menu'),
        },
        coachmark: {
            close: t('veneer:close', 'Close'),
            information: t('veneer:information', '%{start} of %{end}'),
            previous: t('veneer:previous', 'Previous'),
            next: t('veneer:next', 'Next'),
        },
        datePicker: {
            clear: t('veneer:clear', 'Clear'),
            nextMonth: t('veneer:nextMonth', 'next month'),
            nextYear: t('veneer:nextYear', 'next year'),
            previousMonth: t('veneer:previousMonth', 'previous month'),
            previousYear: t('veneer:previousYear', 'previous year'),
            selecting: t('veneer:selecting', 'Selecting'),
            selectMonth: t('veneer:selectMonth', 'Select month'),
            selectYear: t('veneer:selectYear', 'Select year'),
        },
        inlineNotification: {
            close: t('veneer:close', 'Close'),
        },
        notification: {
            close: t('veneer:close', 'Close'),
        },
        pagination: {
            currentPage: t('veneer:currentPage', 'Page %{pageNumber} (Current Page)'),
            goto: t('veneer:gotoPage', 'Go to page %{pageNumber}'),
            next: t('veneer:next', 'Next'),
            pageSizeInformation: t('veneer:pageSizeInformation', 'of %{total}'),
            paginationNavigation: t('veneer:paginationNavigation', 'Pagination Navigation'),
            previous: t('veneer:previous', 'Previous'),
            selectPageSize: t('veneer:selectPageSize', 'Select page size'),
        },
        password: {
            requirementsErrorTitle: t('veneer:requirementsErrorTitle', 'Check password requirements'),
            toggleButtonAriaLabel: t('veneer:toggleButtonAriaLabel', 'Toggle password visibility'),
        },
        search: {
            clear: t('veneer:clear', 'Clear'),
            showingResult: t('veneer:showingResult', 'Showing %s result'),
            showingResults: t('veneer:showingResults', 'Showing %s results'),
        },
        select: {
            clear: t('veneer:clear', 'Clear'),
            noResults: t('veneer:noResults', 'No results found'),
            open: t('veneer:open', 'Open'),
            searchPlaceholder: t('veneer:searchPlaceholder', 'Search Items'),
            selected: t('veneer:selected', 'Selected'),
            showingResult: t('veneer:showingResult', 'Showing %s result'),
            showingResults: t('veneer:showingResults', 'Showing %s results'),
            unselected: t('veneer:unselected', 'Unselected'),
        },
        sideBar: {
            backdrop: t('veneer:backdrop', 'Backdrop'),
            close: t('veneer:close', 'Close'),
            collapse: t('veneer:collapse', 'Collapse'),
            expand: t('veneer:expand', 'Expand'),
        },
        sideMenu: {
            toggle: t('veneer:toggle', 'Toggle Menu'),
        },
        stepper: {
            currentStep: t('veneer:currentStep', 'Step %{stepNumber} (Current Step)'),
            goto: t('veneer:gotoStep', 'Go to step %{stepNumber}'),
            stepLabel: t('veneer:stepLabel', 'Step %{stepNumber}'),
        },
        table: {
            actionButton: t('veneer:actionMenu', 'action menu'),
            cancelButton: t('veneer:cancelButton', 'Cancel'),
            clearAllFilters: t('veneer:clearAllFilters', 'Clear Filters'),
            columnOptions: t('veneer:columnOptions', 'Column Options'),
            columnResizeTooltip: t('veneer:columnResizeTooltip', 'Width: %{columnWidth} px'),
            deselectAllItems: t('veneer:deselectAllItems', 'Deselect all'),
            downButton: t('veneer:downButton', 'move selected column down'),
            itemSelected: t('veneer:itemSelected', '%s item selected'),
            itemsSelected: t('veneer:itemsSelected', '%s items selected'),
            noItems: t('veneer:noItems', 'No Items Found'),
            resetToDefault: t('veneer:resetToDefault', 'Reset to Default'),
            saveButton: t('veneer:saveButton', 'Save'),
            selectAllItems: t('veneer:selectAllItems', 'Select all %{totalNumberOfItems} items'),
            selectAllPageItems: t('veneer:selectAllPageItems', 'select all items in the current page'),
            sortedAscending: t('veneer:sortedAscending', 'ascending sorted'),
            sortedDescending: t('veneer:sortedDescending', 'descending sorted'),
            upButton: t('veneer:upButton', 'move selected column up'),
        },
        tabs: {
            scrollStartButton: t('veneer:scrollStartButton', 'Scroll Start'),
            scrollEndButton: t('veneer:scrollEndButton', 'Scroll End'),
        },
        toastNotification: {
            close: t('veneer:close', 'Close'),
        },
    };

    return (
        <GlobalTranslationsContext.Provider
            value={{
                general: generalTranslations,
                veneer: veneerTranslations,
            }}
        >
            {children}
        </GlobalTranslationsContext.Provider>
    );
};
